import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { readString } from 'react-papaparse'
import {
    Alert,
    Badge,
    Box,
    Button,
    ColumnLayout,
    Container,
    ContentLayout,
    FileUpload,
    FormField,
    Header,
    ProgressBar,
    SelectProps,
    SpaceBetween,
    StatusIndicator,
    Table,
    Wizard,
} from '@amzn/awsui-components-react'
import HeaderTemplate from '../reusable/HeaderTemplate'
import FilterableDataTable from '../reusable/FilterableDataTable'
import { HEADER_BG_COLOR } from '../../Constant'
import {
    buildHrVarianceColumnDefinitions,
    CheckResult,
    EXPECTED_HEADERS,
    FILE_UPLOAD_I18N_STRINGS,
    HrVariance,
    IMPORT_ERROR_COLUMN_DEFINITIONS,
    ImportErrorRow,
    MAX_FILE_SIZE_BYTES,
    ROW_VALIDATION_COLUMN_DEFINITIONS,
    RowValidationError,
    RowValidationResult,
    STEP_DOWNLOAD,
    STEP_REVIEW,
    STEP_SUBMISSION,
    STEP_UPLOAD,
    SubmissionStatus,
    TEMPLATE_EXAMPLE_ROWS,
    TEMPLATE_PREVIEW_COLUMN_DEFINITIONS,
    ValidationChecks,
    ValidationStatus,
    VarianceReasonState,
    WIZARD_I18N_STRINGS,
} from './ImportAllocationPageConstants'
import {
    buildHrVarianceReasons,
    buildImportErrorRows,
    buildRowValidationResults,
    checkResultToStatusType,
    downloadAnnotatedCsv,
    downloadSubmittedCsv,
    downloadTemplateCsv,
    downloadUploadedCsvWithErrors,
    encodeBase64Utf8,
    formatCountLabel,
    incompleteVarianceKeys,
    sanitizeUploadedCsvText,
    SummaryStatus,
    SummaryStatusField,
    validateDataTypes,
    validateRequiredFields,
    writeReasonsIntoComments,
} from './ImportAllocationPageUtil'
import { useAppContext } from '../../../context'
import useStore from '../../Store'

const ImportAllocationPage = () => {
    const navigate = useNavigate()
    const appContext = useAppContext()
    const apiClient = appContext.apiClient
    const selectBusinessEntity = useStore((state) => state.selectBusinessEntity)

    const [activeStepIndex, setActiveStepIndex] = useState(STEP_DOWNLOAD)
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
    const [isValidating, setIsValidating] = useState(false)
    const [validationStatus, setValidationStatus] = useState<ValidationStatus>(
        ValidationStatus.IDLE,
    )
    const [checks, setChecks] = useState<ValidationChecks | null>(null)
    const [csvText, setCsvText] = useState('')
    const [dataRows, setDataRows] = useState<string[][]>([])
    const [rowResults, setRowResults] = useState<RowValidationResult[]>([])
    const [rowValidationRan, setRowValidationRan] = useState(false)

    const [serverValidateStatus, setServerValidateStatus] = useState<SubmissionStatus>(
        SubmissionStatus.IDLE,
    )
    const [submitStatus, setSubmitStatus] = useState<SubmissionStatus>(SubmissionStatus.IDLE)
    const [uploadProgress, setUploadProgress] = useState(0)
    const [submissionMessage, setSubmissionMessage] = useState('')
    const [dataValidationPassed, setDataValidationPassed] = useState(false)
    const [importErrorRows, setImportErrorRows] = useState<ImportErrorRow[]>([])
    const [errorFileDownloaded, setErrorFileDownloaded] = useState(false)
    const [hrVariances, setHrVariances] = useState<HrVariance[]>([])
    const [reasonStateByKey, setReasonStateByKey] = useState<Record<string, VarianceReasonState>>(
        {},
    )

    const validateFile = async (
        file: File,
    ): Promise<{
        checks: ValidationChecks
        csvText: string
        dataRows: string[][]
        rowResults: RowValidationResult[]
        rowValidationRan: boolean
    }> => {
        const sizeResult: CheckResult =
            file.size <= MAX_FILE_SIZE_BYTES ? CheckResult.PASS : CheckResult.FAIL

        if (sizeResult === CheckResult.FAIL) {
            return {
                checks: {
                    size: CheckResult.FAIL,
                    content: CheckResult.PASS,
                    headers: CheckResult.PASS,
                    data: CheckResult.PASS,
                    rows: CheckResult.PASS,
                },
                csvText: '',
                dataRows: [],
                rowResults: [],
                rowValidationRan: false,
            }
        }

        const rawText = (await file.text()).trim()
        const text = rawText.length > 0 ? sanitizeUploadedCsvText(rawText) : rawText
        const contentResult: CheckResult = text.length > 0 ? CheckResult.PASS : CheckResult.FAIL

        let rows: string[][] = []
        if (contentResult === CheckResult.PASS) {
            readString<string[]>(text, {
                skipEmptyLines: true,
                complete: (results: { data: string[][] }) => {
                    rows = results.data
                },
            })
        }

        const header = rows.length > 0 ? rows[0].map((cell) => cell.trim()) : []
        const headersMatch =
            header.length >= EXPECTED_HEADERS.length &&
            EXPECTED_HEADERS.every(
                (expected, index) => header[index].toLowerCase() === expected.toLowerCase(),
            )
        const headersResult: CheckResult = headersMatch ? CheckResult.PASS : CheckResult.FAIL
        const dataRows = rows.slice(1)
        const dataResult: CheckResult = dataRows.length > 0 ? CheckResult.PASS : CheckResult.FAIL

        const canValidateRows =
            headersResult === CheckResult.PASS && dataResult === CheckResult.PASS
        const allRowErrors: RowValidationError[] = canValidateRows
            ? [...validateRequiredFields(dataRows), ...validateDataTypes(dataRows)]
            : []
        const rowResults = canValidateRows
            ? buildRowValidationResults(dataRows.length, allRowErrors)
            : []

        return {
            checks: {
                size: sizeResult,
                content: contentResult,
                headers: headersResult,
                data: dataResult,
                rows: allRowErrors.length === 0 ? CheckResult.PASS : CheckResult.FAIL,
            },
            csvText: text,
            dataRows,
            rowResults,
            rowValidationRan: canValidateRows,
        }
    }

    const runValidation = async (): Promise<ValidationStatus> => {
        const result = await validateFile(uploadedFiles[0])
        setChecks(result.checks)
        setCsvText(result.csvText)
        setDataRows(result.dataRows)
        setRowResults(result.rowResults)
        setRowValidationRan(result.rowValidationRan)
        const status: ValidationStatus =
            result.checks.size === CheckResult.PASS &&
            result.checks.content === CheckResult.PASS &&
            result.checks.headers === CheckResult.PASS &&
            result.checks.data === CheckResult.PASS &&
            result.checks.rows === CheckResult.PASS
                ? ValidationStatus.SUCCESS
                : ValidationStatus.ERROR
        setValidationStatus(status)
        return status
    }

    const onValidate = async () => {
        setIsValidating(true)
        await runValidation()
        setIsValidating(false)
        setActiveStepIndex(STEP_REVIEW)
    }

    const resetServerState = () => {
        setServerValidateStatus(SubmissionStatus.IDLE)
        setSubmitStatus(SubmissionStatus.IDLE)
        setUploadProgress(0)
        setSubmissionMessage('')
        setDataValidationPassed(false)
        setImportErrorRows([])
        setErrorFileDownloaded(false)
        setHrVariances([])
        setReasonStateByKey({})
    }

    const trackUploadProgress = (event: { loaded: number; total?: number }) => {
        if (event.total) {
            setUploadProgress(Math.round((event.loaded * 100) / event.total))
        }
    }

    const importErrorRowsFromPayload = (
        payload:
            | {
                  validation_result?: {
                      errors?: {
                          row_number: number | null
                          row_numbers: number[] | null
                          column: string | null
                          message: string
                      }[]
                  }
              }
            | undefined,
    ): ImportErrorRow[] =>
        payload?.validation_result
            ? buildImportErrorRows(payload.validation_result.errors ?? [])
            : []

    const runServerValidation = async (file: File): Promise<boolean> => {
        setServerValidateStatus(SubmissionStatus.IN_PROGRESS)
        setSubmitStatus(SubmissionStatus.IDLE)
        setUploadProgress(0)
        setSubmissionMessage('')
        setImportErrorRows([])
        setErrorFileDownloaded(false)
        setHrVariances([])
        try {
            const contentBase64 = encodeBase64Utf8(csvText)
            const response = await apiClient.post(
                `/allocations/business-entity/${selectBusinessEntity.id}/bulk-upload/validate`,
                { filename: file.name, content_base64: contentBase64 },
                { onUploadProgress: trackUploadProgress },
            )
            const { validation_result, hr_validation_result } = response.data
            const isValid = validation_result?.is_valid ?? false
            setDataValidationPassed(isValid)
            setImportErrorRows(isValid ? [] : buildImportErrorRows(validation_result?.errors ?? []))
            setHrVariances(
                hr_validation_result?.has_variance
                    ? ((hr_validation_result.variances ?? []) as HrVariance[])
                    : [],
            )
            setServerValidateStatus(isValid ? SubmissionStatus.SUCCESS : SubmissionStatus.ERROR)
            return isValid
        } catch (error) {
            console.error(error)
            const payload = (
                error as {
                    response?: {
                        data?: {
                            message?: string
                            validation_result?: {
                                errors?: {
                                    row_number: number | null
                                    row_numbers: number[] | null
                                    column: string | null
                                    message: string
                                }[]
                            }
                        }
                    }
                }
            )?.response?.data
            setDataValidationPassed(false)
            setSubmissionMessage(payload?.message ?? '')
            setImportErrorRows(importErrorRowsFromPayload(payload))
            setServerValidateStatus(SubmissionStatus.ERROR)
            return false
        }
    }

    const startSubmit = async () => {
        const file = uploadedFiles[0]
        if (!file) {
            return
        }
        setSubmitStatus(SubmissionStatus.IN_PROGRESS)
        setUploadProgress(0)
        setSubmissionMessage('')
        setImportErrorRows([])
        try {
            const annotatedText = writeReasonsIntoComments(csvText, hrVariances, reasonStateByKey)
            downloadSubmittedCsv(annotatedText, file.name)
            const contentBase64 = encodeBase64Utf8(annotatedText)
            const response = await apiClient.post(
                `/allocations/business-entity/${selectBusinessEntity.id}/bulk-upload/submit`,
                {
                    filename: file.name,
                    content_base64: contentBase64,
                    hr_variance_reasons: buildHrVarianceReasons(hrVariances, reasonStateByKey),
                },
                { onUploadProgress: trackUploadProgress },
            )
            setSubmissionMessage(response.data?.message ?? '')
            setSubmitStatus(SubmissionStatus.SUCCESS)
        } catch (error) {
            console.error(error)
            const payload = (
                error as {
                    response?: {
                        data?: {
                            message?: string
                            validation_result?: {
                                errors?: {
                                    row_number: number | null
                                    row_numbers: number[] | null
                                    column: string | null
                                    message: string
                                }[]
                            }
                        }
                    }
                }
            )?.response?.data
            setSubmissionMessage(payload?.message ?? '')
            setImportErrorRows(importErrorRowsFromPayload(payload))
            setSubmitStatus(SubmissionStatus.ERROR)
        }
    }

    const onReasonChange = (key: string, reason: SelectProps.Option | null) => {
        setReasonStateByKey((prev) => ({
            ...prev,
            [key]: { reason, note: prev[key]?.note ?? '' },
        }))
    }

    const onNoteChange = (key: string, note: string) => {
        setReasonStateByKey((prev) => ({
            ...prev,
            [key]: { reason: prev[key]?.reason ?? null, note },
        }))
    }

    const resetValidation = (files: File[]) => {
        setUploadedFiles(files)
        setValidationStatus(ValidationStatus.IDLE)
        setChecks(null)
        setCsvText('')
        setDataRows([])
        setRowResults([])
        setRowValidationRan(false)
        resetServerState()
    }

    const hasFile = uploadedFiles.length > 0
    const validationSucceeded = validationStatus === ValidationStatus.SUCCESS

    const previousBusinessEntityId = useRef(selectBusinessEntity.id)
    useEffect(() => {
        if (previousBusinessEntityId.current === selectBusinessEntity.id) {
            return
        }
        previousBusinessEntityId.current = selectBusinessEntity.id
        setActiveStepIndex(STEP_DOWNLOAD)
        resetValidation([])
    }, [selectBusinessEntity.id])

    useEffect(() => {
        if (
            activeStepIndex === STEP_SUBMISSION &&
            hasFile &&
            serverValidateStatus === SubmissionStatus.IDLE
        ) {
            void runServerValidation(uploadedFiles[0])
        }
    }, [activeStepIndex])

    const onNavigate = (requestedStepIndex: number) => {
        if (requestedStepIndex >= STEP_REVIEW && !checks) {
            return
        }
        if (requestedStepIndex >= STEP_SUBMISSION && !validationSucceeded) {
            return
        }
        setActiveStepIndex(requestedStepIndex)
    }

    const overallErrorCount = checks
        ? checks.size === CheckResult.FAIL
            ? 1
            : [checks.content, checks.headers, checks.data].filter(
                  (result) => result === CheckResult.FAIL,
              ).length
        : 0

    const rowPassCount = rowResults.filter((row) => row.result === CheckResult.PASS).length
    const rowFailCount = rowResults.length - rowPassCount

    const validating = serverValidateStatus === SubmissionStatus.IN_PROGRESS
    const validateFailed = serverValidateStatus === SubmissionStatus.ERROR
    const submitting = submitStatus === SubmissionStatus.IN_PROGRESS
    const submitSucceeded = submitStatus === SubmissionStatus.SUCCESS
    const submitFailed = submitStatus === SubmissionStatus.ERROR
    const busy = validating || submitting
    const hasImportErrors = importErrorRows.length > 0
    const hasVariances = hrVariances.length > 0
    const incompleteKeys = incompleteVarianceKeys(hrVariances, reasonStateByKey)
    const reasonsComplete = incompleteKeys.length === 0
    const hrVarianceColumnDefinitions = buildHrVarianceColumnDefinitions(
        reasonStateByKey,
        onReasonChange,
        onNoteChange,
    )
    const showHrTable = hasVariances && !submitSucceeded
    const showDataErrorTable = hasImportErrors

    const dataValidationStatus: SummaryStatus = validating
        ? { type: 'in-progress', text: 'In progress' }
        : hasImportErrors || validateFailed
          ? { type: 'error', text: 'Failed' }
          : dataValidationPassed
            ? { type: 'success', text: 'Passed' }
            : { type: 'pending', text: 'Not started' }
    const hrVarianceStatus: SummaryStatus = validating
        ? { type: 'in-progress', text: 'In progress' }
        : !dataValidationPassed
          ? { type: 'pending', text: 'Not started' }
          : hasVariances
            ? {
                  type: reasonsComplete ? 'success' : 'warning',
                  text: `${hrVariances.length} variance${hrVariances.length === 1 ? '' : 's'}${
                      reasonsComplete ? ' — reasons provided' : ' — reasons required'
                  }`,
              }
            : { type: 'success', text: 'No variances' }

    const renderPrimaryActions = () => {
        const previousButton = (
            <Button onClick={() => setActiveStepIndex(activeStepIndex - 1)}>Previous</Button>
        )
        switch (activeStepIndex) {
            case STEP_DOWNLOAD:
                return (
                    <Button variant='primary' onClick={() => setActiveStepIndex(STEP_UPLOAD)}>
                        Next
                    </Button>
                )
            case STEP_UPLOAD:
                return (
                    <SpaceBetween direction='horizontal' size='xs'>
                        {previousButton}
                        <Button
                            variant='primary'
                            loading={isValidating}
                            disabled={!hasFile}
                            disabledReason='Upload an allocation CSV file before validating.'
                            onClick={onValidate}
                        >
                            Validate file
                        </Button>
                    </SpaceBetween>
                )
            case STEP_REVIEW:
                return (
                    <SpaceBetween direction='horizontal' size='xs'>
                        {previousButton}
                        <Button
                            variant='primary'
                            disabled={!validationSucceeded}
                            disabledReason='Validation must pass with no errors before continuing. Correct the file and re-validate in the previous step.'
                            onClick={() => setActiveStepIndex(STEP_SUBMISSION)}
                        >
                            Next
                        </Button>
                    </SpaceBetween>
                )
            case STEP_SUBMISSION: {
                if (submitSucceeded) {
                    return (
                        <Button variant='primary' onClick={() => navigate('/')}>
                            Go to home
                        </Button>
                    )
                }
                if (validating) {
                    return (
                        <Button variant='primary' loading disabled>
                            Validating
                        </Button>
                    )
                }
                if (submitting) {
                    return (
                        <Button variant='primary' loading disabled>
                            Submitting
                        </Button>
                    )
                }
                if (hasImportErrors) {
                    return (
                        <SpaceBetween direction='horizontal' size='xs'>
                            {previousButton}
                            <Button
                                variant='primary'
                                iconName='upload'
                                disabled={!reasonsComplete || !errorFileDownloaded}
                                disabledReason={
                                    !reasonsComplete
                                        ? 'Provide a reason for every HR variance before re-uploading.'
                                        : 'Download the CSV with data validation errors before re-uploading.'
                                }
                                onClick={() => setActiveStepIndex(STEP_UPLOAD)}
                            >
                                Re-Upload Fixed Report
                            </Button>
                        </SpaceBetween>
                    )
                }
                if (validateFailed) {
                    return (
                        <SpaceBetween direction='horizontal' size='xs'>
                            {previousButton}
                            <Button
                                variant='primary'
                                onClick={() => runServerValidation(uploadedFiles[0])}
                            >
                                Retry validation
                            </Button>
                        </SpaceBetween>
                    )
                }
                return (
                    <SpaceBetween direction='horizontal' size='xs'>
                        {previousButton}
                        <Button
                            variant='primary'
                            disabled={!dataValidationPassed || !reasonsComplete}
                            disabledReason={
                                !dataValidationPassed
                                    ? 'Data validation must pass before submitting.'
                                    : 'Provide a reason for every HR variance before submitting.'
                            }
                            onClick={startSubmit}
                        >
                            {submitFailed ? 'Retry submit' : 'Submit'}
                        </Button>
                    </SpaceBetween>
                )
            }
            default:
                return null
        }
    }

    return (
        <ContentLayout
            defaultPadding
            headerBackgroundStyle={HEADER_BG_COLOR}
            headerVariant='high-contrast'
            header={
                <Box margin={{ left: 's', right: 's' }}>
                    <SpaceBetween size='s' direction='vertical'>
                        <HeaderTemplate
                            subtitle='Download the CSV template, fill in your allocation data, and upload the completed file for validation and import.'
                            items={[
                                { text: 'Home', href: '/' },
                                { text: 'Import Allocation CSV', href: '' },
                            ]}
                        />
                    </SpaceBetween>
                </Box>
            }
        >
            <Box margin={{ left: 's', right: 's' }}>
                <Container>
                    <Wizard
                        activeStepIndex={activeStepIndex}
                        onNavigate={({ detail }) => onNavigate(detail.requestedStepIndex)}
                        onCancel={() => navigate('/')}
                        i18nStrings={WIZARD_I18N_STRINGS}
                        secondaryActions={
                            <Button variant='link' disabled={busy} onClick={() => navigate('/')}>
                                Cancel
                            </Button>
                        }
                        customPrimaryActions={renderPrimaryActions()}
                        steps={[
                            {
                                title: 'Download',
                                description:
                                    'Download the CSV template to see the required format and column structure, then fill in your allocation data following the template.',
                                content: (
                                    <Container
                                        header={
                                            <Header
                                                variant='h2'
                                                actions={
                                                    <Button
                                                        iconName='download'
                                                        variant='normal'
                                                        onClick={() => downloadTemplateCsv()}
                                                    >
                                                        Download template
                                                    </Button>
                                                }
                                            >
                                                Allocation CSV template
                                            </Header>
                                        }
                                    >
                                        <SpaceBetween size='m'>
                                            <Box variant='p' color='text-body-secondary'>
                                                The template includes all required and optional
                                                columns. Each row represents one allocation record
                                                to import.
                                            </Box>
                                            <Box variant='strong' color='text-status-warning'>
                                                Do not modify the column headers. Your file must use
                                                the exact header names shown in the table.
                                            </Box>
                                            <Table
                                                variant='embedded'
                                                header={
                                                    <Header
                                                        variant='h3'
                                                        description='Review the expected column structure and example values before filling in your data.'
                                                    >
                                                        Template Preview
                                                    </Header>
                                                }
                                                columnDefinitions={
                                                    TEMPLATE_PREVIEW_COLUMN_DEFINITIONS
                                                }
                                                items={TEMPLATE_EXAMPLE_ROWS}
                                            />
                                        </SpaceBetween>
                                    </Container>
                                ),
                            },
                            {
                                title: 'Upload',
                                description:
                                    'Upload your completed allocation CSV file, then select Validate file to check it before import.',
                                content: (
                                    <Container>
                                        <FormField
                                            label='Allocation CSV file'
                                            description='Select the allocation CSV file you want to import. The file must match the downloaded template format.'
                                            constraintText='Accepted format: .csv. Maximum file size: 1 MB. Only one file can be uploaded.'
                                        >
                                            <FileUpload
                                                value={uploadedFiles}
                                                multiple={false}
                                                accept='.csv'
                                                showFileSize
                                                showFileLastModified
                                                i18nStrings={FILE_UPLOAD_I18N_STRINGS}
                                                onChange={({ detail }) =>
                                                    resetValidation(detail.value)
                                                }
                                            />
                                        </FormField>
                                    </Container>
                                ),
                            },
                            {
                                title: 'Review file format validations',
                                description:
                                    'Review the results of each validation check. Correct any errors in your allocation file and re-validate before submitting.',
                                content: checks && (
                                    <SpaceBetween size='l'>
                                        {validationSucceeded ? (
                                            <Alert
                                                type='success'
                                                header='File validated successfully'
                                            >
                                                Your allocation file passed all validation checks.
                                                Select Submit to import it.
                                            </Alert>
                                        ) : (
                                            <Alert type='error' header='Validation failed'>
                                                Your allocation file contains errors. Review the
                                                results below, correct the issues, and re-validate
                                                the file in the previous step.
                                            </Alert>
                                        )}
                                        <Container
                                            header={
                                                <Header
                                                    variant='h2'
                                                    counter={formatCountLabel(overallErrorCount)}
                                                    description='Results of the structural checks performed on the uploaded file.'
                                                >
                                                    Overall validations
                                                </Header>
                                            }
                                        >
                                            <SpaceBetween size='m'>
                                                <StatusIndicator
                                                    type={checkResultToStatusType(checks.size)}
                                                >
                                                    {checks.size === CheckResult.PASS
                                                        ? 'File size — passed: the file is within the 1 MB limit'
                                                        : 'File size — failed: the file exceeds the 1 MB limit'}
                                                </StatusIndicator>
                                                {checks.size === CheckResult.PASS && (
                                                    <>
                                                        <StatusIndicator
                                                            type={checkResultToStatusType(
                                                                checks.content,
                                                            )}
                                                        >
                                                            {checks.content === CheckResult.PASS
                                                                ? 'File content — passed: the file is not empty'
                                                                : 'File content — failed: the file is empty'}
                                                        </StatusIndicator>
                                                        <StatusIndicator
                                                            type={checkResultToStatusType(
                                                                checks.headers,
                                                            )}
                                                        >
                                                            {checks.headers === CheckResult.PASS
                                                                ? 'Column template — passed: the first columns match the template names and order'
                                                                : 'Column template — failed: the first columns must match the template names and order (extra trailing columns are allowed)'}
                                                        </StatusIndicator>
                                                        <StatusIndicator
                                                            type={checkResultToStatusType(
                                                                checks.data,
                                                            )}
                                                        >
                                                            {checks.data === CheckResult.PASS
                                                                ? 'Data completeness — passed: the file contains at least one allocation record'
                                                                : 'Data completeness — failed: the file contains no allocation records'}
                                                        </StatusIndicator>
                                                    </>
                                                )}
                                            </SpaceBetween>
                                        </Container>
                                        <Container
                                            header={
                                                <Header
                                                    variant='h2'
                                                    description='Row-level results for data content and data type checks.'
                                                    actions={
                                                        rowValidationRan &&
                                                        rowFailCount > 0 && (
                                                            <Button
                                                                iconName='download'
                                                                onClick={() =>
                                                                    downloadAnnotatedCsv(
                                                                        dataRows,
                                                                        rowResults,
                                                                    )
                                                                }
                                                            >
                                                                Download CSV with Errors Indications
                                                            </Button>
                                                        )
                                                    }
                                                >
                                                    <SpaceBetween
                                                        direction='horizontal'
                                                        size='xs'
                                                        alignItems='center'
                                                    >
                                                        <span>Row-by-row content validation</span>
                                                        {rowValidationRan && (
                                                            <>
                                                                <Badge color='green'>
                                                                    {rowPassCount} passed
                                                                </Badge>
                                                                <Badge color='red'>
                                                                    {rowFailCount} failed
                                                                </Badge>
                                                            </>
                                                        )}
                                                    </SpaceBetween>
                                                </Header>
                                            }
                                        >
                                            {rowValidationRan ? (
                                                <FilterableDataTable
                                                    items={rowResults}
                                                    columnDefinitions={
                                                        ROW_VALIDATION_COLUMN_DEFINITIONS
                                                    }
                                                    emptyText='No allocation records to validate.'
                                                    itemNoun='rows'
                                                    filteringPlaceholder='Find rows'
                                                    filteringAriaLabel='Filter row-by-row validation results'
                                                />
                                            ) : (
                                                <Alert type='info' header='Row validation skipped'>
                                                    Row-by-row content validation did not run
                                                    because the overall validations above failed.
                                                    Fix the issues listed under Overall validations,
                                                    then re-validate the file to see per-row
                                                    results.
                                                </Alert>
                                            )}
                                        </Container>
                                    </SpaceBetween>
                                ),
                            },
                            {
                                title: 'Submit',
                                description:
                                    'Your file is validated against the server automatically. Resolve any data-validation errors or HR variances, then submit to import.',
                                content: (
                                    <SpaceBetween size='l'>
                                        <Container
                                            header={
                                                <Header
                                                    variant='h2'
                                                    description='Review your import details before submitting.'
                                                >
                                                    Import summary
                                                </Header>
                                            }
                                        >
                                            <SpaceBetween size='l'>
                                                <ColumnLayout columns={5} variant='text-grid'>
                                                    <div>
                                                        <Box variant='awsui-key-label'>
                                                            File name
                                                        </Box>
                                                        <div>{uploadedFiles[0]?.name ?? '-'}</div>
                                                    </div>
                                                    <div>
                                                        <Box variant='awsui-key-label'>
                                                            Total rows
                                                        </Box>
                                                        <div>{rowResults.length}</div>
                                                    </div>
                                                    <SummaryStatusField
                                                        label='Format validation'
                                                        status={{
                                                            type: 'success',
                                                            text: 'Passed',
                                                        }}
                                                    />
                                                    <SummaryStatusField
                                                        label='Data validation'
                                                        status={dataValidationStatus}
                                                    />
                                                    <SummaryStatusField
                                                        label='HR variances'
                                                        status={hrVarianceStatus}
                                                    />
                                                </ColumnLayout>
                                                {validating ? (
                                                    <Alert type='info' header='Validating file'>
                                                        Checking your allocation file against the
                                                        server. This will not import anything yet.
                                                    </Alert>
                                                ) : submitSucceeded ? (
                                                    <Alert type='success' header='Import complete'>
                                                        {submissionMessage ||
                                                            'Your allocation file was imported successfully.'}{' '}
                                                        A copy of the submitted CSV has been
                                                        downloaded to your device.
                                                    </Alert>
                                                ) : hasImportErrors ? (
                                                    <Alert
                                                        type='error'
                                                        header='Data validation failed'
                                                    >
                                                        {submissionMessage ||
                                                            'Your file did not pass data validation and nothing was saved. Review the errors below, then re-upload a corrected file.'}
                                                    </Alert>
                                                ) : validateFailed ? (
                                                    <Alert
                                                        type='error'
                                                        header='Could not validate file'
                                                    >
                                                        {submissionMessage ||
                                                            'The validation request failed. This looks like a network or server error — select Retry validation to try again.'}
                                                    </Alert>
                                                ) : submitFailed ? (
                                                    <Alert type='error' header='Import failed'>
                                                        {submissionMessage ||
                                                            'The import failed and no allocations were saved. Select Retry submit to try again.'}
                                                    </Alert>
                                                ) : hasVariances && !reasonsComplete ? (
                                                    <Alert
                                                        type='warning'
                                                        header='HR variance detected'
                                                    >
                                                        Your file passed data validation, but some
                                                        allocations disagree with HR headcount.
                                                        Provide a reason for each variance below,
                                                        then select Submit to import.
                                                    </Alert>
                                                ) : (
                                                    <Alert type='info'>
                                                        Your file passed data validation. Choosing
                                                        Submit will import your allocations. This
                                                        action cannot be undone.
                                                    </Alert>
                                                )}
                                            </SpaceBetween>
                                        </Container>
                                        {(validating || submitting) && (
                                            <ProgressBar
                                                value={uploadProgress}
                                                label={
                                                    validating
                                                        ? 'Validating file'
                                                        : 'Uploading file'
                                                }
                                                description='Your CSV file is being uploaded and processed. Do not leave this page.'
                                            />
                                        )}
                                        {showHrTable && (
                                            <Container
                                                header={
                                                    <Header
                                                        variant='h2'
                                                        counter={formatCountLabel(
                                                            hrVariances.length,
                                                        )}
                                                        description='These allocations differ from HR headcount. Select a reason for each before submitting. This does not block import once every reason is provided.'
                                                    >
                                                        HR Variances
                                                    </Header>
                                                }
                                            >
                                                <SpaceBetween size='s'>
                                                    {!reasonsComplete && (
                                                        <Alert type='info'>
                                                            {incompleteKeys.length} of{' '}
                                                            {hrVariances.length} variance
                                                            {hrVariances.length === 1
                                                                ? ''
                                                                : 's'}{' '}
                                                            still{' '}
                                                            {incompleteKeys.length === 1
                                                                ? 'needs'
                                                                : 'need'}{' '}
                                                            a reason.
                                                        </Alert>
                                                    )}
                                                    <FilterableDataTable
                                                        items={hrVariances}
                                                        columnDefinitions={
                                                            hrVarianceColumnDefinitions
                                                        }
                                                        emptyText='No HR variances to display.'
                                                        itemNoun='variances'
                                                        filteringPlaceholder='Find variances'
                                                        filteringAriaLabel='Filter HR variances'
                                                    />
                                                </SpaceBetween>
                                            </Container>
                                        )}
                                        {showDataErrorTable && (
                                            <Container
                                                header={
                                                    <Header
                                                        variant='h2'
                                                        description='The import server rejected these rows. No allocations were saved. Download the CSV with the errors, correct the file, and re-upload.'
                                                        counter={formatCountLabel(
                                                            importErrorRows.length,
                                                        )}
                                                        actions={
                                                            <Button
                                                                variant='primary'
                                                                iconName='download'
                                                                onClick={() => {
                                                                    downloadUploadedCsvWithErrors(
                                                                        writeReasonsIntoComments(
                                                                            csvText,
                                                                            hrVariances,
                                                                            reasonStateByKey,
                                                                        ),
                                                                        importErrorRows,
                                                                    )
                                                                    setErrorFileDownloaded(true)
                                                                }}
                                                            >
                                                                Download CSV with Data Validation
                                                                Errors
                                                            </Button>
                                                        }
                                                    >
                                                        Data Validation Errors
                                                    </Header>
                                                }
                                            >
                                                <SpaceBetween size='s'>
                                                    <FilterableDataTable
                                                        items={importErrorRows}
                                                        columnDefinitions={
                                                            IMPORT_ERROR_COLUMN_DEFINITIONS
                                                        }
                                                        emptyText='No import errors to display.'
                                                        itemNoun='rows'
                                                        filteringPlaceholder='Find rows'
                                                        filteringAriaLabel='Filter import errors'
                                                    />
                                                </SpaceBetween>
                                            </Container>
                                        )}
                                    </SpaceBetween>
                                ),
                            },
                        ]}
                    />
                </Container>
            </Box>
        </ContentLayout>
    )
}

export default ImportAllocationPage
